import {loadExternalScript} from './externalScript';

/**
 * Surface of the recruiter chat SDKs. Sticky chat, my-chat and video-chat
 * bundles each merge their methods into the same `window.chatObject`, so
 * loading one never clobbers another.
 */
export interface RecruiterChatObject {
  stickyChat?: (config: Record<string, unknown>) => void;
  myChat?: (config: Record<string, unknown>) => void;
  videoChat?: (config: Record<string, unknown>) => void;
  initiateChat?: (config: Record<string, unknown>) => void;
}

type ChatWindow = Window & {chatObject?: RecruiterChatObject};

const getChatObject = (): RecruiterChatObject | undefined =>
  typeof window === 'undefined' ? undefined : (window as ChatWindow).chatObject;

const loadChatSdk = async (
  src: string | undefined,
  method: keyof RecruiterChatObject
): Promise<RecruiterChatObject | undefined> => {
  if (getChatObject()?.[method]) return getChatObject();
  await loadExternalScript(src);
  return getChatObject();
};

/** Sticky-chat SDK — provides `stickyChat` and `initiateChat`. */
export const loadStickyChatSdk = () =>
  loadChatSdk(process.env.NEXT_PUBLIC_CHAT_RECRUITER_SDK, 'stickyChat');

/** My-chat SDK — the `/chats` page widget. */
export const loadMyChatSdk = () =>
  loadChatSdk(process.env.NEXT_PUBLIC_MYCHATS_RECRUITER_SDK, 'myChat');

/** Video-chat SDK — the `/interview/meet` widget. */
export const loadVideoChatSdk = () =>
  loadChatSdk(process.env.NEXT_PUBLIC_VIDEO_CHAT_RECRUITER_SDK, 'videoChat');

/**
 * Opens a chat thread with a recruiter, loading the sticky-chat SDK first if
 * this page hasn't needed it yet. Fails soft: a blocked or failed SDK load
 * leaves the click a no-op, exactly as the old `if (!chatObject) return` did.
 */
export const initiateRecruiterChat = (config: Record<string, unknown>) =>
  loadChatSdk(process.env.NEXT_PUBLIC_CHAT_RECRUITER_SDK, 'initiateChat')
    .then(chat => chat?.initiateChat?.(config))
    .catch(error => console.error('[recruiter-chat] SDK load failed:', error));
